import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { fmtRupiah, fmtDate, fmtOrEmpty } from "@/lib/format";
import { FinancialSummary } from "@/components/FinancialSummary";
import { toast } from "sonner";
import { ArrowLeft, Loader2, FileSpreadsheet, Boxes, PackageOpen, AlertTriangle, PlusCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { MATERIAL_USAGE_RECORD, PROFIT_VIEW } from "@/lib/constants";

const STATUS_BADGE = { Aman: "bg-emerald-50 text-emerald-700", Waspada: "bg-amber-50 text-amber-700", Rugi: "bg-red-50 text-red-700", "Belum Lengkap": "bg-slate-100 text-slate-600" };

export default function HppDetail() {
  const { projectUuid, vehicleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canRecord = user && MATERIAL_USAGE_RECORD.includes(user.role);
  const canProfit = user && PROFIT_VIEW.includes(user.role);
  const [h, setH] = useState(null);
  const [usages, setUsages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/hpp/${projectUuid}/${vehicleId}`);
      setH(data);
      const u = await api.get("/material-usage", { params: { project_uuid: projectUuid } });
      setUsages(u.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); navigate("/hpp"); }
    finally { setLoading(false); }
  }, [projectUuid, vehicleId, navigate]);
  useEffect(() => { load(); }, [load]);

  if (loading || !h) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const over = h.budget_status === "Over Budget";
  const saving = h.budget_status === "Saving";

  return (
    <div className="space-y-4 pb-20" data-testid="hpp-detail-page">
      <div className="flex items-center gap-3">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)} data-testid="back-button"><ArrowLeft size={18} /></Button>
        <div><div className="font-mono-k3 text-sm font-bold text-primary">{h.project_id}</div><div className="text-sm font-semibold text-navy">{h.plate} · {h.brand} {h.model} · {h.customer_name}</div></div>
        {h.status && <span className={`ml-auto text-xs px-2.5 py-1 rounded-full ${STATUS_BADGE[h.status]}`} data-testid="hpp-status">{h.status}</span>}
      </div>

      {!canProfit && (
        <Card className="p-4 text-sm text-muted-foreground" data-testid="hpp-money-hidden">Nominal biaya, RAB, HPP, dan margin tidak ditampilkan untuk role Anda. Daftar pemakaian material (jumlah) tetap tersedia di bawah.</Card>
      )}
      {canProfit && (
      <Card className="p-4">
        <h3 className="font-heading font-semibold text-navy text-sm mb-3">Kontrol Biaya Proyek (Penawaran · RAB · HPP)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">Nilai Penawaran</div><div className="font-bold text-navy" data-testid="hpp-offer">{fmtOrEmpty(h.offer_value_net)}</div></div>
          <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">RAP (Rencana: Bahan + Sparepart + Jasa)</div><div className="font-bold text-navy" data-testid="hpp-rab">{fmtOrEmpty(h.rap_plan)}</div></div>
          <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">HPP Aktual (Material Sheet)</div><div className="font-bold text-navy" data-testid="hpp-actual">{fmtOrEmpty(h.hpp_actual)}</div></div>
          <div className={`rounded-md border p-3 ${over ? "border-red-300 bg-red-50" : saving ? "border-emerald-300 bg-emerald-50" : ""}`}>
            <div className="text-xs text-muted-foreground">Selisih RAP − HPP</div>
            <div className={`font-bold ${h.variance == null ? "text-slate-500 text-sm" : h.variance < 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="hpp-variance">{fmtOrEmpty(h.variance)}</div>
            <div className={`text-[10px] mt-0.5 font-semibold ${over ? "text-red-600" : "text-emerald-700"}`} data-testid="hpp-budget-status">{h.budget_status}</div>
          </div>
        </div>
        {over && (
          <div className="mt-3 flex items-center gap-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="hpp-overwarn">
            <AlertTriangle size={14} /> HPP aktual melebihi RAB — biaya produksi <b>OVER BUDGET</b> {fmtRupiah(Math.abs(h.variance))}.
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3" data-testid="hpp-cost-breakdown">
          <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">Material Aktual</div><div className="font-bold text-navy" data-testid="hpp-material-cost">{fmtOrEmpty(h.hpp_actual)}</div></div>
          <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">Biaya Tenaga Kerja</div><div className="font-bold text-slate-500 text-sm" data-testid="hpp-labor-cost">{fmtOrEmpty(h.labor_cost)}</div></div>
          <div className="rounded-md border p-3"><div className="text-xs text-muted-foreground">Biaya Produksi Lainnya</div><div className="font-bold text-slate-500 text-sm" data-testid="hpp-other-cost">{fmtOrEmpty(h.other_cost)}</div></div>
        </div>
        <div className="text-[11px] text-muted-foreground mt-2" data-testid="hpp-last-date">Tanggal pencatatan terakhir: {h.last_usage_date ? fmtDate(h.last_usage_date) : "BELUM TERISI"}</div>
      </Card>
      )}
      {canProfit && <FinancialSummary h={h} />}

      {/* Material Sheet */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div><h3 className="font-heading font-semibold text-navy text-sm">Material Sheet (Sumber HPP)</h3><p className="text-xs text-muted-foreground">{canProfit ? `HPP = akumulasi ${h.material_count} pemakaian bahan aktual. RAB tidak dipakai sebagai HPP.` : `${h.material_count} pemakaian bahan tercatat.`}</p></div>
          {canRecord && <Button size="sm" className="bg-primary hover:bg-red-700" data-testid="hpp-add-usage" onClick={() => navigate(`/material-usage?project=${projectUuid}`)}><PlusCircle size={14} className="mr-1.5" /> Catat Pemakaian</Button>}
        </div>
        {usages.length === 0 ? (
          <div className="text-center text-muted-foreground py-8 text-sm" data-testid="hpp-usage-empty"><PackageOpen className="mx-auto mb-2 opacity-40" /> Belum ada pemakaian bahan dicatat. HPP: BELUM TERISI.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100"><tr className="text-left text-xs">
                <th className="p-2">Tanggal</th><th className="p-2">Material</th><th className="p-2 text-right">Qty</th><th className="p-2">Satuan</th>
                <th className="p-2 text-right">Harga</th><th className="p-2 text-right">Total</th><th className="p-2">Sumber</th><th className="p-2">PIC</th>
              </tr></thead>
              <tbody>
                {usages.map((m) => (
                  <tr key={m.id} className="border-t" data-testid={`hpp-usage-${m.id}`}>
                    <td className="p-2 whitespace-nowrap">{fmtDate(m.used_date)}</td>
                    <td className="p-2"><div className="font-medium">{m.material_name}</div><div className="text-xs text-muted-foreground">{m.category}</div></td>
                    <td className="p-2 text-right">{m.qty}</td><td className="p-2">{m.unit}</td>
                    <td className="p-2 text-right">{m.price != null ? fmtRupiah(m.price) : "—"}</td>
                    <td className="p-2 text-right font-medium">{m.total != null ? fmtRupiah(m.total) : "—"}</td>
                    <td className="p-2"><span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100">{m.source}</span></td>
                    <td className="p-2 text-xs text-muted-foreground">{m.pic}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot><tr className="border-t bg-slate-50 font-bold"><td className="p-2" colSpan={5}>Total HPP Aktual</td><td className="p-2 text-right" data-testid="hpp-usage-total">{canProfit ? fmtOrEmpty(h.hpp_actual) : "—"}</td><td colSpan={2}></td></tr></tfoot>
            </table>
          </div>
        )}
      </Card>

      {/* Drilldown */}
      <Card className="p-4">
        <h3 className="font-heading font-semibold text-navy text-sm mb-2">Drilldown Sumber</h3>
        <div className="flex flex-wrap gap-2">
          {h.estimate_id ? <Button variant="outline" size="sm" onClick={() => navigate(`/estimates/${h.estimate_id}`)} data-testid="hpp-goto-estimate"><FileSpreadsheet size={14} className="mr-1.5" /> Penawaran {h.estimate_no}</Button> : <span className="text-xs text-muted-foreground">Belum ada penawaran disetujui</span>}
          {h.rab_id ? <Button variant="outline" size="sm" onClick={() => navigate(`/rab/${h.rab_id}`)} data-testid="hpp-goto-rab"><Boxes size={14} className="mr-1.5" /> Buka RAB Bahan</Button> : <span className="text-xs text-muted-foreground">Belum ada RAB</span>}
        </div>
      </Card>
    </div>
  );
}
